import * as Sentry from '@sentry/react'
import { IndexedDBStorage } from '../storage/IndexedDBStorage'

// Use a different database name to avoid conflicts with LocationDataSearch.ts
const STORAGE_CONFIG = {
    dbName: 'LocationSearchDBv2',
    storeName: 'locations',
    version: 1,
}

// Keys for IndexedDB storage
const LOCATIONS_KEY = 'all_locations'
const LOCATIONS_INDEX_KEY = 'locationIndex'
const LOCATIONS_VERSION_KEY = 'locationsVersion'

// ================= Data types =================
export enum LocationType {
    CITY = 'city',
    STATE = 'state',
    ZONE = 'zone',
}
export interface CityLocationData {
    id: string
    stateCode: string
    stateName: string
    type: LocationType
    city: string
    county: string
    latitude: number
    longitude: number
    /** Present when the option came from an exact five-digit ZIP lookup. */
    postalCode?: string
}

export interface StateLocationData {
    id: string
    stateCode: string
    stateName: string
    type: LocationType
}

export interface ZoneLocationData {
    id: string
    zoneCode: string
    states: string
    type: LocationType.ZONE
}

// DAT zone-to-states mapping (source of truth: @numeo/types)
import { ZONE_STATES as ZONE_STATES_ARRAY } from '@numeo/types-core'

/** String version for UI display (e.g. "CT, MA, ME, NH, NJ, RI, VT") */
export const ZONE_STATES: Record<string, string> = Object.fromEntries(
    [...Object.entries(ZONE_STATES_ARRAY), ['ZM', ['Mexico']]].map(([k, v]) => [k, (v as string[]).join(', ')])
)

// Special city codes mapping - common abbreviations people use (airport codes, common nicknames)
// Exported so all location search components can use the same list
export const SPECIAL_CITY_CODES: Record<string, CityLocationData> = {
    NYC: {
        id: 'city-nyc',
        city: 'New York City',
        stateCode: 'NY',
        stateName: 'New York',
        type: LocationType.CITY,
        county: 'New York',
        latitude: 40.7128,
        longitude: -74.006,
    },
    LA: {
        id: 'city-la',
        city: 'Los Angeles',
        stateCode: 'CA',
        stateName: 'California',
        type: LocationType.CITY,
        county: 'Los Angeles',
        latitude: 34.0522,
        longitude: -118.2437,
    },
    SF: {
        id: 'city-sf',
        city: 'San Francisco',
        stateCode: 'CA',
        stateName: 'California',
        type: LocationType.CITY,
        county: 'San Francisco',
        latitude: 37.7749,
        longitude: -122.4194,
    },
    CHI: {
        id: 'city-chi',
        city: 'Chicago',
        stateCode: 'IL',
        stateName: 'Illinois',
        type: LocationType.CITY,
        county: 'Cook',
        latitude: 41.8781,
        longitude: -87.6298,
    },
    ATL: {
        id: 'city-atl',
        city: 'Atlanta',
        stateCode: 'GA',
        stateName: 'Georgia',
        type: LocationType.CITY,
        county: 'Fulton',
        latitude: 33.749,
        longitude: -84.388,
    },
    DFW: {
        id: 'city-dfw',
        city: 'Dallas',
        stateCode: 'TX',
        stateName: 'Texas',
        type: LocationType.CITY,
        county: 'Dallas',
        latitude: 32.7767,
        longitude: -96.797,
    },
    PHX: {
        id: 'city-phx',
        city: 'Phoenix',
        stateCode: 'AZ',
        stateName: 'Arizona',
        type: LocationType.CITY,
        county: 'Maricopa',
        latitude: 33.4484,
        longitude: -112.074,
    },
    DEN: {
        id: 'city-den',
        city: 'Denver',
        stateCode: 'CO',
        stateName: 'Colorado',
        type: LocationType.CITY,
        county: 'Denver',
        latitude: 39.7392,
        longitude: -104.9903,
    },
    SEA: {
        id: 'city-sea',
        city: 'Seattle',
        stateCode: 'WA',
        stateName: 'Washington',
        type: LocationType.CITY,
        county: 'King',
        latitude: 47.6062,
        longitude: -122.3321,
    },
    MIA: {
        id: 'city-mia',
        city: 'Miami',
        stateCode: 'FL',
        stateName: 'Florida',
        type: LocationType.CITY,
        county: 'Miami-Dade',
        latitude: 25.7617,
        longitude: -80.1918,
    },
    BOS: {
        id: 'city-bos',
        city: 'Boston',
        stateCode: 'MA',
        stateName: 'Massachusetts',
        type: LocationType.CITY,
        county: 'Suffolk',
        latitude: 42.3601,
        longitude: -71.0589,
    },
    LV: {
        id: 'city-lv',
        city: 'Las Vegas',
        stateCode: 'NV',
        stateName: 'Nevada',
        type: LocationType.CITY,
        county: 'Clark',
        latitude: 36.1699,
        longitude: -115.1398,
    },
    NOLA: {
        id: 'city-nola',
        city: 'New Orleans',
        stateCode: 'LA',
        stateName: 'Louisiana',
        type: LocationType.CITY,
        county: 'Orleans',
        latitude: 29.9511,
        longitude: -90.0715,
    },
    DC: {
        id: 'city-dc',
        city: 'Washington',
        stateCode: 'DC',
        stateName: 'District of Columbia',
        type: LocationType.CITY,
        county: 'District of Columbia',
        latitude: 38.9072,
        longitude: -77.0369,
    },
    PHL: {
        id: 'city-phl',
        city: 'Philadelphia',
        stateCode: 'PA',
        stateName: 'Pennsylvania',
        type: LocationType.CITY,
        county: 'Philadelphia',
        latitude: 39.9526,
        longitude: -75.1652,
    },
    HOU: {
        id: 'city-hou',
        city: 'Houston',
        stateCode: 'TX',
        stateName: 'Texas',
        type: LocationType.CITY,
        county: 'Harris',
        latitude: 29.7604,
        longitude: -95.3698,
    },
    SAT: {
        id: 'city-sat',
        city: 'San Antonio',
        stateCode: 'TX',
        stateName: 'Texas',
        type: LocationType.CITY,
        county: 'Bexar',
        latitude: 29.4241,
        longitude: -98.4936,
    },
    SAN: {
        id: 'city-san',
        city: 'San Diego',
        stateCode: 'CA',
        stateName: 'California',
        type: LocationType.CITY,
        county: 'San Diego',
        latitude: 32.7157,
        longitude: -117.1611,
    },
    PDX: {
        id: 'city-pdx',
        city: 'Portland',
        stateCode: 'OR',
        stateName: 'Oregon',
        type: LocationType.CITY,
        county: 'Multnomah',
        latitude: 45.5152,
        longitude: -122.6784,
    },
    MSP: {
        id: 'city-msp',
        city: 'Minneapolis',
        stateCode: 'MN',
        stateName: 'Minnesota',
        type: LocationType.CITY,
        county: 'Hennepin',
        latitude: 44.9778,
        longitude: -93.265,
    },
    DTW: {
        id: 'city-dtw',
        city: 'Detroit',
        stateCode: 'MI',
        stateName: 'Michigan',
        type: LocationType.CITY,
        county: 'Wayne',
        latitude: 42.3314,
        longitude: -83.0458,
    },
    BNA: {
        id: 'city-bna',
        city: 'Nashville',
        stateCode: 'TN',
        stateName: 'Tennessee',
        type: LocationType.CITY,
        county: 'Davidson',
        latitude: 36.1627,
        longitude: -86.7816,
    },
    CLT: {
        id: 'city-clt',
        city: 'Charlotte',
        stateCode: 'NC',
        stateName: 'North Carolina',
        type: LocationType.CITY,
        county: 'Mecklenburg',
        latitude: 35.2271,
        longitude: -80.8431,
    },
    IND: {
        id: 'city-ind',
        city: 'Indianapolis',
        stateCode: 'IN',
        stateName: 'Indiana',
        type: LocationType.CITY,
        county: 'Marion',
        latitude: 39.7684,
        longitude: -86.1581,
    },
    CMH: {
        id: 'city-cmh',
        city: 'Columbus',
        stateCode: 'OH',
        stateName: 'Ohio',
        type: LocationType.CITY,
        county: 'Franklin',
        latitude: 39.9612,
        longitude: -82.9988,
    },
    AUS: {
        id: 'city-aus',
        city: 'Austin',
        stateCode: 'TX',
        stateName: 'Texas',
        type: LocationType.CITY,
        county: 'Travis',
        latitude: 30.2672,
        longitude: -97.7431,
    },
    JAX: {
        id: 'city-jax',
        city: 'Jacksonville',
        stateCode: 'FL',
        stateName: 'Florida',
        type: LocationType.CITY,
        county: 'Duval',
        latitude: 30.3322,
        longitude: -81.6557,
    },
    FTW: {
        id: 'city-ftw',
        city: 'Fort Worth',
        stateCode: 'TX',
        stateName: 'Texas',
        type: LocationType.CITY,
        county: 'Tarrant',
        latitude: 32.7555,
        longitude: -97.3308,
    },
    OKC: {
        id: 'city-okc',
        city: 'Oklahoma City',
        stateCode: 'OK',
        stateName: 'Oklahoma',
        type: LocationType.CITY,
        county: 'Oklahoma',
        latitude: 35.4676,
        longitude: -97.5164,
    },
    MEM: {
        id: 'city-mem',
        city: 'Memphis',
        stateCode: 'TN',
        stateName: 'Tennessee',
        type: LocationType.CITY,
        county: 'Shelby',
        latitude: 35.1495,
        longitude: -90.049,
    },
    SDF: {
        id: 'city-sdf',
        city: 'Louisville',
        stateCode: 'KY',
        stateName: 'Kentucky',
        type: LocationType.CITY,
        county: 'Jefferson',
        latitude: 38.2527,
        longitude: -85.7585,
    },
    BWI: {
        id: 'city-bwi',
        city: 'Baltimore',
        stateCode: 'MD',
        stateName: 'Maryland',
        type: LocationType.CITY,
        county: 'Baltimore City',
        latitude: 39.2904,
        longitude: -76.6122,
    },
    MKE: {
        id: 'city-mke',
        city: 'Milwaukee',
        stateCode: 'WI',
        stateName: 'Wisconsin',
        type: LocationType.CITY,
        county: 'Milwaukee',
        latitude: 43.0389,
        longitude: -87.9065,
    },
    ABQ: {
        id: 'city-abq',
        city: 'Albuquerque',
        stateCode: 'NM',
        stateName: 'New Mexico',
        type: LocationType.CITY,
        county: 'Bernalillo',
        latitude: 35.0844,
        longitude: -106.6504,
    },
    TUS: {
        id: 'city-tus',
        city: 'Tucson',
        stateCode: 'AZ',
        stateName: 'Arizona',
        type: LocationType.CITY,
        county: 'Pima',
        latitude: 32.2226,
        longitude: -110.9747,
    },
    MCI: {
        id: 'city-mci',
        city: 'Kansas City',
        stateCode: 'MO',
        stateName: 'Missouri',
        type: LocationType.CITY,
        county: 'Jackson',
        latitude: 39.0997,
        longitude: -94.5786,
    },
    SLC: {
        id: 'city-slc',
        city: 'Salt Lake City',
        stateCode: 'UT',
        stateName: 'Utah',
        type: LocationType.CITY,
        county: 'Salt Lake',
        latitude: 40.7608,
        longitude: -111.891,
    },
    TPA: {
        id: 'city-tpa',
        city: 'Tampa',
        stateCode: 'FL',
        stateName: 'Florida',
        type: LocationType.CITY,
        county: 'Hillsborough',
        latitude: 27.9506,
        longitude: -82.4572,
    },
    MCO: {
        id: 'city-mco',
        city: 'Orlando',
        stateCode: 'FL',
        stateName: 'Florida',
        type: LocationType.CITY,
        county: 'Orange',
        latitude: 28.5383,
        longitude: -81.3792,
    },
    PIT: {
        id: 'city-pit',
        city: 'Pittsburgh',
        stateCode: 'PA',
        stateName: 'Pennsylvania',
        type: LocationType.CITY,
        county: 'Allegheny',
        latitude: 40.4406,
        longitude: -79.9959,
    },
    CLE: {
        id: 'city-cle',
        city: 'Cleveland',
        stateCode: 'OH',
        stateName: 'Ohio',
        type: LocationType.CITY,
        county: 'Cuyahoga',
        latitude: 41.4993,
        longitude: -81.6944,
    },
    CVG: {
        id: 'city-cvg',
        city: 'Cincinnati',
        stateCode: 'OH',
        stateName: 'Ohio',
        type: LocationType.CITY,
        county: 'Hamilton',
        latitude: 39.1031,
        longitude: -84.512,
    },
    STL: {
        id: 'city-stl',
        city: 'St. Louis',
        stateCode: 'MO',
        stateName: 'Missouri',
        type: LocationType.CITY,
        county: 'St. Louis City',
        latitude: 38.627,
        longitude: -90.1994,
    },
    RDU: {
        id: 'city-rdu',
        city: 'Raleigh',
        stateCode: 'NC',
        stateName: 'North Carolina',
        type: LocationType.CITY,
        county: 'Wake',
        latitude: 35.7796,
        longitude: -78.6382,
    },
}

const storage = new IndexedDBStorage(STORAGE_CONFIG)

/** Set by the first failed call: a profile that cannot use IndexedDB fails every call, so stop calling it. */
let cacheBroken = false

/**
 * IndexedDB is only a cache for the parsed CSV and the built index. Some browser profiles cannot open or write it
 * (site data blocked by policy, a corrupted backing store, no quota), so every cache call degrades to a miss or a
 * no-op there and the index is built in memory from the CSV instead of search silently returning nothing.
 */
const cache = {
    isUsable: (): boolean => !cacheBroken,
    async get<T>(key: string): Promise<T | undefined> {
        if (cacheBroken) return undefined
        try {
            return await storage.get<T>(key)
        } catch (error) {
            markCacheBroken('get', error)
            return undefined
        }
    },
    async set(key: string, value: unknown): Promise<void> {
        if (cacheBroken) return
        try {
            await storage.set(key, value)
        } catch (error) {
            markCacheBroken('set', error)
        }
    },
    async clear(): Promise<void> {
        if (cacheBroken) return
        try {
            await storage.clear()
        } catch (error) {
            markCacheBroken('clear', error)
        }
    },
}

const markCacheBroken = (operation: string, error: unknown): void => {
    if (cacheBroken) return
    cacheBroken = true
    console.warn(`Location search cache ${operation} failed, using in-memory index for this page load:`, error)
    Sentry.captureException(error, { tags: { feature: 'location-search', operation: `cache-${operation}` } })
}

/** How long a failed load blocks the next attempt, so a persistent failure does not refetch the CSV on every search. */
const INIT_RETRY_DELAY_MS = 30_000

interface TrieNode {
    children: Map<string, TrieNode>
    isEndOfWord: boolean
    word?: string
}

class RadixTree {
    private root: TrieNode

    constructor() {
        this.root = { children: new Map(), isEndOfWord: false }
    }

    insert(word: string): void {
        let current = this.root
        const normalizedWord = word.toLowerCase()

        for (const char of normalizedWord) {
            if (!current.children.has(char)) {
                current.children.set(char, {
                    children: new Map(),
                    isEndOfWord: false,
                })
            }
            current = current.children.get(char)!
        }
        current.isEndOfWord = true
        current.word = word
    }

    findWordsWithPrefix(prefix: string): string[] {
        const words: string[] = []
        let current = this.root
        const normalizedPrefix = prefix.toLowerCase()

        // Navigate to the node representing the prefix
        for (const char of normalizedPrefix) {
            if (!current.children.has(char)) {
                return words
            }
            current = current.children.get(char)!
        }

        // Collect all words from this node
        this.collectWords(current, words)
        return words
    }

    private collectWords(node: TrieNode, words: string[]): void {
        if (node.isEndOfWord && node.word) {
            words.push(node.word)
        }

        for (const [, child] of node.children) {
            this.collectWords(child, words)
        }
    }

    size(): number {
        let count = 0
        const traverse = (node: TrieNode) => {
            if (node.isEndOfWord) count++
            for (const child of node.children.values()) {
                traverse(child)
            }
        }
        traverse(this.root)
        return count
    }
}

interface SearchMetrics {
    query: string
    resultCount: number
    durationMs: number
    timestamp: number
}

const METRICS_KEY = 'searchMetrics'
const MAX_METRICS = 100

/**
 * The search order and techniques used in the LocationDataSearchV2 class are as follows:
 * Search Order
 * Prefix Search:
 * - Attribute: city (and potentially county if included in the search logic).
 * - Technique: Utilizes a Radix Tree for efficient prefix matching. This allows quick lookups for locations starting with a given string.
 * N-Gram Search:
 * - Attribute: city (and potentially county).
 * - Technique: Uses N-gram indexing, which breaks down the city names into smaller substrings (n-grams) to facilitate substring searches. This is particularly useful for partial matches.
 * Inverted Index Search:
 * - Attribute: stateCode and potentially county.
 * - Technique: An inverted index is used to map each unique term (like state codes) to the locations that contain them, allowing for fast lookups based on exact matches.
 * Summary of Techniques
 * - Radix Tree: For efficient prefix matching to quickly find locations that start with a specified string.
 * - N-Gram Indexing: To support substring searches, allowing for more flexible matching against city names.
 * - Inverted Index: To enable quick lookups based on exact matches to state codes or other attributes.
 * This structured approach allows the search functionality to be both efficient and flexible, catering to various user input scenarios. If you have further questions or need additional details, feel free to ask!
 */
class LocationDataSearchV2 {
    // Core data structures - Space: O(n) where n = number of locations
    private invertedIndex: Map<string, Set<string>>
    private radixTree: RadixTree
    private ngramIndex: Map<string, Set<string>>
    private locations: Map<string, CityLocationData>
    private readonly NGRAM_SIZE = 3
    private statesMap: Map<string, StateLocationData> = new Map()

    private initializePromise: Promise<void> | null = null
    private lastInitFailure: { at: number; error: unknown } | null = null
    private initFailureReported = false

    constructor() {
        this.invertedIndex = new Map()
        this.radixTree = new RadixTree()
        this.ngramIndex = new Map()
        this.locations = new Map()
        // Failures are reported inside initialize, and search retries it.
        this.initialize().catch(() => undefined)
    }

    // Refactored initialize method
    public async initialize(): Promise<void> {
        // Deduplicate concurrent calls — reuse the same promise
        if (this.initializePromise) return this.initializePromise
        // A failed load is retried by a later search, not kept for the whole session, but only after a delay
        if (this.lastInitFailure && Date.now() - this.lastInitFailure.at < INIT_RETRY_DELAY_MS) throw this.lastInitFailure.error
        this.initializePromise = this._doInitialize().catch((error: unknown) => {
            this.initializePromise = null
            this.lastInitFailure = { at: Date.now(), error }
            if (!this.initFailureReported) {
                this.initFailureReported = true
                Sentry.captureException(error, { tags: { feature: 'location-search', operation: 'initialize' } })
            }
            throw error
        })
        return this.initializePromise
    }

    private async _doInitialize(): Promise<void> {
        // Check if we need to update the locations data
        await this.checkAndUpdateLocationsData()

        const savedData = await cache.get<string>(LOCATIONS_INDEX_KEY)

        if (savedData && this.tryDeserialize(savedData)) return

        const locations = await this.loadLocations()

        locations.forEach((location) => {
            this.addLocation(location)

            const stateCode = location.stateCode.toUpperCase()
            if (!this.statesMap.has(stateCode)) {
                this.statesMap.set(stateCode, {
                    id: `state-${stateCode}`,
                    stateCode: stateCode,
                    stateName: location.stateName,
                    type: LocationType.STATE,
                })
            }
        })

        // Cache the serialized index so subsequent loads are instant. Serializing is costly, so skip it without a cache.
        if (cache.isUsable()) await cache.set(LOCATIONS_INDEX_KEY, this.serialize())
    }

    /** A corrupted or empty cached index is rebuilt from the CSV rather than leaving search empty. */
    private tryDeserialize(savedData: string): boolean {
        try {
            this.deserialize(savedData)
            if (this.locations.size > 0) return true
        } catch (error) {
            console.warn('Cached location index is unreadable, rebuilding:', error)
        }
        this.clear()
        this.statesMap.clear()
        return false
    }

    // Time: O(1) amortized hash table lookups
    private getLocationString(location: CityLocationData): string {
        return `${location.city.toLowerCase()} ${location.stateCode.toLowerCase()}`
    }

    private generateNgrams(text: string): string[] {
        const normalized = text.toLowerCase()
        const ngrams: string[] = []
        for (let i = 0; i <= normalized.length - this.NGRAM_SIZE; i++) {
            ngrams.push(normalized.slice(i, i + this.NGRAM_SIZE))
        }
        return ngrams
    }

    private tokenize(text: string): string[] {
        return text
            .toLowerCase()
            .split(/[\s,.-]+/)
            .filter((word) => word.length > 0)
    }

    // Time: O(k) where k = query length (radix tree traversal)
    // Space: O(1) - constant intermediate storage
    private searchPrefix(query: string): Set<string> {
        const prefixMatches = this.radixTree.findWordsWithPrefix(query.toLowerCase())
        const results = new Set<string>()
        prefixMatches.forEach((word) => {
            const locationIds = this.invertedIndex.get(word.toLowerCase())
            if (locationIds) {
                locationIds.forEach((id) => results.add(id))
            }
        })
        return results
    }

    // Time: O(m) where m = number of n-grams in query
    // Space: O(p) where p = number of potential matches
    private searchNgrams(query: string): Set<string> {
        const queryNgrams = this.generateNgrams(query.toLowerCase())
        const ngramMatches = new Set<string>()
        let isFirst = true

        for (const ngram of queryNgrams) {
            const locationIds = this.ngramIndex.get(ngram)
            if (locationIds) {
                if (isFirst) {
                    locationIds.forEach((id) => ngramMatches.add(id))
                    isFirst = false
                } else {
                    // Intersect with previous matches
                    for (const id of ngramMatches) {
                        if (!locationIds.has(id)) {
                            ngramMatches.delete(id)
                        }
                    }
                }
            } else {
                // If any ngram is not found, no matches are possible
                ngramMatches.clear()
                break
            }
        }

        // Verify matches contain the full query string
        for (const id of ngramMatches) {
            const location = this.locations.get(id)
            if (location) {
                const locationString = this.getLocationString(location)
                if (locationString.toLowerCase().includes(query.toLowerCase())) {
                    ngramMatches.add(id)
                }
            }
        }

        return ngramMatches
    }

    // Time: O(q) where q = number of space-separated terms in query
    // Space: O(r) where r = number of matching documents per term
    private searchInvertedIndex(query: string): Set<string> {
        const words = this.tokenize(query)
        const results = new Set<string>()
        words.forEach((word) => {
            const locationIds = this.invertedIndex.get(word.toLowerCase())
            if (locationIds) {
                locationIds.forEach((id) => results.add(id))
            }
        })
        return results
    }

    /** Levenshtein edit distance — used for fuzzy/typo-tolerant search */
    private editDistance(a: string, b: string): number {
        const m = a.length
        const n = b.length
        // Use single-row DP to save memory
        let prev = Array.from({ length: n + 1 }, (_, i) => i)
        for (let i = 1; i <= m; i++) {
            const curr = [i]
            for (let j = 1; j <= n; j++) {
                curr[j] = a[i - 1] === b[j - 1] ? prev[j - 1] : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1])
            }
            prev = curr
        }
        return prev[n]
    }

    /** Fuzzy search — finds cities within edit distance threshold of the query.
     *  Only runs when exact strategies return few results.
     *  Caps both total iterations (3000) and matches (50) to stay off the critical path. */
    private searchFuzzy(query: string, maxDistance: number = 2): Set<string> {
        const results = new Set<string>()
        const queryLower = query.toLowerCase()
        const queryTokens = queryLower.split(/[\s,]+/).filter((t) => t.length > 2)
        const cityQuery = queryTokens.length > 0 ? queryTokens[0] : queryLower

        let iterations = 0
        const MAX_ITERATIONS = 3000

        for (const [id, location] of this.locations) {
            if (++iterations > MAX_ITERATIONS || results.size >= 50) break
            const cityLower = location.city.toLowerCase()
            // Quick length check — edit distance can't be less than length difference
            if (Math.abs(cityLower.length - cityQuery.length) > maxDistance) continue
            const cityFirstWord = cityLower.split(' ')[0]
            const dist = Math.min(this.editDistance(cityQuery, cityLower), this.editDistance(cityQuery, cityFirstWord))
            if (dist <= maxDistance) {
                results.add(id)
            }
        }
        return results
    }

    // Overall search complexity:
    // Time: O(k + m + q) - combination of all search methods
    // Space: O(s) where s = total unique results from all methods
    public async search(query: string): Promise<CityLocationData[]> {
        if (!query) {
            return []
        }

        if (query.length <= 2) {
            // Search only by state codes and return state codes only.
            throw new Error('Query too short. Please use searchByStateCodes instead.')
        }

        // Callers search straight after mount, before the index has loaded
        await this.initialize()

        const start = performance.now()
        const normalizedQuery = query.toLowerCase()
        // Extract city-only part for sorting (e.g. "columbs oh" → "columbs")
        const queryTokens = normalizedQuery.split(/[\s,]+/).filter((t) => t.length > 0)
        const cityQueryForSort = queryTokens.filter((t) => t.length > 2)[0] || normalizedQuery
        const stateFilter = queryTokens.find((t) => t.length <= 2)?.toUpperCase()
        const results = new Set<string>()

        // Strategy 1: Prefix matching using radix tree
        const prefixMatches = this.searchPrefix(normalizedQuery)
        prefixMatches.forEach((id) => results.add(id))

        // Strategy 2: Ngram matching for substring search
        if (normalizedQuery.length >= this.NGRAM_SIZE) {
            const ngramMatches = this.searchNgrams(normalizedQuery)
            ngramMatches.forEach((id) => results.add(id))
        }

        // Strategy 3: Inverted index search
        const invertedIndexMatches = this.searchInvertedIndex(normalizedQuery)
        invertedIndexMatches.forEach((id) => results.add(id))

        // Strategy 4: Fuzzy search — catches typos like "columbs" → "columbus"
        // Only runs when exact strategies found few results to avoid flooding
        if (results.size < 5 && cityQueryForSort.length >= 4) {
            const maxDist = cityQueryForSort.length <= 5 ? 1 : 2
            const fuzzyMatches = this.searchFuzzy(normalizedQuery, maxDist)
            fuzzyMatches.forEach((id) => results.add(id))
        }

        // Convert results to Location objects, pre-compute edit distances, then sort
        const candidates = Array.from(results)
            .map((id) => this.locations.get(id)!)
            .filter(Boolean)

        // Pre-compute edit distance once per candidate (avoids O(n log n) recomputation in sort)
        const distMap = new Map<string, number>()
        for (const loc of candidates) {
            const firstWord = loc.city.toLowerCase().split(' ')[0]
            distMap.set(loc.id, this.editDistance(cityQueryForSort, firstWord))
        }

        const resultArray = candidates.sort((a, b) => {
            const aLower = a.city.toLowerCase()
            const bLower = b.city.toLowerCase()

            // If user typed a state code, prioritize matching state
            if (stateFilter) {
                const aStateMatch = a.stateCode.toUpperCase() === stateFilter
                const bStateMatch = b.stateCode.toUpperCase() === stateFilter
                if (aStateMatch !== bStateMatch) return aStateMatch ? -1 : 1
            }

            // Check if the city name matches its state name (e.g., New York, NY)
            const aCityMatchesState = aLower === a.stateName.toLowerCase()
            const bCityMatchesState = bLower === b.stateName.toLowerCase()
            if (aCityMatchesState !== bCityMatchesState) return aCityMatchesState ? -1 : 1

            // Exact city name match
            const aExact = aLower === cityQueryForSort
            const bExact = bLower === cityQueryForSort
            if (aExact !== bExact) return aExact ? -1 : 1

            // City starts with query
            const aStarts = aLower.startsWith(cityQueryForSort)
            const bStarts = bLower.startsWith(cityQueryForSort)
            if (aStarts !== bStarts) return aStarts ? -1 : 1

            // Any word starts with query
            const aWordStarts = aLower.split(' ').some((w) => w.startsWith(cityQueryForSort))
            const bWordStarts = bLower.split(' ').some((w) => w.startsWith(cityQueryForSort))
            if (aWordStarts !== bWordStarts) return aWordStarts ? -1 : 1

            // Edit distance — closer typo matches rank higher (pre-computed)
            const aDist = distMap.get(a.id) ?? 999
            const bDist = distMap.get(b.id) ?? 999
            if (aDist !== bDist) return aDist - bDist

            // Substring match
            const aContains = this.getLocationString(a).toLowerCase().includes(normalizedQuery)
            const bContains = this.getLocationString(b).toLowerCase().includes(normalizedQuery)
            if (aContains !== bContains) return aContains ? -1 : 1

            return aLower.localeCompare(bLower)
        })

        await this.recordMetric({
            query,
            resultCount: resultArray.length,
            durationMs: performance.now() - start,
        })

        return resultArray
    }

    public searchByStateCodes(query: string): StateLocationData[] {
        // if (query.length > 2) {
        //     throw new Error('Query too short. Please use "search" instead.')
        // }
        const normalizedQuery = query.toUpperCase()
        // Empty prefix = every state. Callers that open a picker before anything is typed want the full
        // list; each existing caller guards on a non-empty query, so this adds a case rather than changing
        // one — the previous behaviour for '' was an empty array via the exact-match branch below.
        if (query.length === 0) {
            return Array.from(this.statesMap.values())
                .sort((a, b) => a.stateCode.localeCompare(b.stateCode))
                .map((l) => ({ id: l.id, stateCode: l.stateCode, stateName: l.stateName, type: l.type }))
        }
        if (query.length === 1) {
            return Array.from(this.statesMap.values())
                .filter((location) => location.stateCode.startsWith(normalizedQuery))
                .sort((a, b) => a.stateCode.localeCompare(b.stateCode))
                .map((l) => ({
                    id: l.id,
                    stateCode: l.stateCode,
                    stateName: l.stateName,
                    type: l.type,
                }))
        } else {
            const state = this.statesMap.get(normalizedQuery)
            return state ? [state] : []
        }
    }

    public searchByZoneCodes(query: string): ZoneLocationData[] {
        const upperQuery = query.trim().toUpperCase()

        const zoneCodes = Object.keys(ZONE_STATES)

        return zoneCodes
            .filter((code) => code.startsWith(upperQuery))
            .map((zoneCode) => ({
                id: `zone-${zoneCode}`,
                zoneCode,
                states: ZONE_STATES[zoneCode],
                type: LocationType.ZONE as const,
            }))
    }

    // Indexing complexity per location:
    // Time: O(w * l^2) where:
    //   w = number of words in location string
    //   l = average length of words
    public addLocation(location: CityLocationData): void {
        this.locations.set(location.id, location)
        const locationString = this.getLocationString(location)
        const words = this.tokenize(locationString)

        // Add to inverted index and radix tree
        words.forEach((word) => {
            if (!this.invertedIndex.has(word)) {
                this.invertedIndex.set(word, new Set())
            }
            this.invertedIndex.get(word)!.add(location.id)
            this.radixTree.insert(word)
        })

        // Add to ngram index
        const ngrams = this.generateNgrams(locationString)
        ngrams.forEach((ngram) => {
            if (!this.ngramIndex.has(ngram)) {
                this.ngramIndex.set(ngram, new Set())
            }
            this.ngramIndex.get(ngram)!.add(location.id)
        })
    }

    // Serialization/Deserialization:
    // Time: O(n) linear traversal of all indexes
    // Space: O(n) storage of all index data in JSON format
    public serialize(): string {
        return JSON.stringify({
            locations: Array.from(this.locations.entries()),
            invertedIndex: Array.from(this.invertedIndex.entries()).map(([key, value]) => [key, Array.from(value)]),
            ngramIndex: Array.from(this.ngramIndex.entries()).map(([key, value]) => [key, Array.from(value)]),
        })
    }

    public deserialize(serialized: string): void {
        const parsed = JSON.parse(serialized)
        this.clear()

        // Restore locations
        parsed.locations.forEach(([id, location]: [string, CityLocationData]) => {
            this.locations.set(id, location)
        })

        // Rebuild indexes
        parsed.invertedIndex.forEach(([word, ids]: [string, string[]]) => {
            this.invertedIndex.set(word, new Set(ids))
        })

        parsed.ngramIndex.forEach(([ngram, ids]: [string, string[]]) => {
            this.ngramIndex.set(ngram, new Set(ids))
        })

        // Rebuild radix tree and statesMap from locations
        Array.from(this.locations.values()).forEach((location) => {
            const locationString = this.getLocationString(location)
            this.tokenize(locationString).forEach((word) => {
                this.radixTree.insert(word)
            })
            const stateCode = location.stateCode.toUpperCase()
            if (!this.statesMap.has(stateCode)) {
                this.statesMap.set(stateCode, {
                    id: `state-${stateCode}`,
                    stateCode: stateCode,
                    stateName: location.stateName,
                    type: LocationType.STATE,
                })
            }
        })
    }

    // CSV Processing:
    // Time: O(m) where m = number of lines in CSV
    // Space: O(m) storing all parsed locations
    /**
     * Checks if the locations data needs to be updated based on version
     * This is called during initialization to ensure users always have the latest data
     */
    private async checkAndUpdateLocationsData(): Promise<void> {
        try {
            // Fetch the current version from the server (cache-bust to always get latest)
            const response = await fetch(`/locations-version.txt?t=${Date.now()}`)
            const currentVersion = (await response.text()).trim()

            // Get the stored version
            const storedVersion = ((await cache.get<string>(LOCATIONS_VERSION_KEY)) ?? '').trim()

            // If version doesn't match, clear all location data to force refresh
            if (storedVersion !== currentVersion) {
                // Clear all location data and index to force full rebuild
                await cache.clear()
            }
        } catch (error) {
            console.error('Failed to check for locations data updates:', error)
        }
    }

    private async loadLocations(): Promise<CityLocationData[]> {
        // Try to load from IndexedDB first
        const cachedData = await cache.get<CityLocationData[]>(LOCATIONS_KEY)

        // If we have data, use it (version check already done in checkAndUpdateLocationsData)
        if (cachedData?.length) {
            return cachedData
        }
        // Parse CSV and store in IndexedDB with version
        const csvData = await this.loadCSVData()
        const parsedData = this.parseCSV(csvData)
        if (parsedData.length === 0) throw new Error('locations.csv contained no locations')

        // Store the data
        await cache.set(LOCATIONS_KEY, parsedData)

        // Store the current version
        try {
            const response = await fetch(`/locations-version.txt?t=${Date.now()}`)
            const currentVersion = (await response.text()).trim()
            await cache.set(LOCATIONS_VERSION_KEY, currentVersion)
        } catch (error) {
            console.error('Failed to fetch locations version:', error)
        }

        return parsedData
    }

    private async loadCSVData(): Promise<string> {
        // Cache-bust to ensure we always get the latest CSV after a version change
        const response = await fetch(`/locations.csv?t=${Date.now()}`)
        if (!response.ok) throw new Error(`locations.csv request failed: ${response.status}`)
        return await response.text()
    }

    private parseCSV(csvText: string): CityLocationData[] {
        const rows = csvText.split('\n')
        const headers = rows[0].split(',').map((header) => header.trim())
        // A proxy, extension or SPA fallback can answer with an HTML page and a 200, whose lines would parse as rows
        if (headers[0]?.toLowerCase() !== 'id') throw new Error(`locations.csv is not a locations CSV: ${rows[0].slice(0, 40)}`)

        const locations: CityLocationData[] = []
        for (let i = 1; i < rows.length; i++) {
            const row = rows[i].split(',')
            if (row.length !== headers.length) continue // Skip malformed rows

            // Remove quotes from all fields
            const cleanRow = row.map((field) => field.trim().replace(/^"|"$/g, ''))

            const locationData: CityLocationData = {
                id: cleanRow[0],
                type: LocationType.CITY,
                city: cleanRow[3],
                stateCode: cleanRow[1],
                stateName: cleanRow[2],
                county: cleanRow[4],
                latitude: parseFloat(cleanRow[5]),
                longitude: parseFloat(cleanRow[6]),
            }
            locations.push(locationData)
        }

        return locations
    }

    // Metrics Handling:
    // Time: O(1) amortized (array push + occasional shift)
    // Space: O(1) fixed-size rolling window
    private async recordMetric(metric: Omit<SearchMetrics, 'timestamp'>): Promise<void> {
        const metrics = (await cache.get<SearchMetrics[]>(METRICS_KEY)) || []
        metrics.push({
            ...metric,
            timestamp: Date.now(),
        })

        if (metrics.length > MAX_METRICS) {
            metrics.shift()
        }

        await cache.set(METRICS_KEY, metrics)
    }

    public async getMetrics(): Promise<SearchMetrics[]> {
        return (await cache.get<SearchMetrics[]>(METRICS_KEY)) || []
    }

    /**
     * The known city closest to a point — names a map area (an H3 cell centroid) for display.
     * A linear scan over ~30k cities (sub-millisecond); call it per area, not per frame.
     */
    public async nearestCity(lat: number, lon: number): Promise<CityLocationData | null> {
        await this.initialize()
        const cosLat = Math.cos((lat * Math.PI) / 180)
        let best: CityLocationData | null = null
        let bestDistance = Infinity
        for (const location of this.locations.values()) {
            const dLat = location.latitude - lat
            const dLon = (location.longitude - lon) * cosLat
            const distance = dLat * dLat + dLon * dLon
            if (distance < bestDistance) {
                bestDistance = distance
                best = location
            }
        }
        return best
    }

    public clear(): void {
        this.invertedIndex.clear()
        this.radixTree = new RadixTree()
        this.ngramIndex.clear()
        this.locations.clear()
    }
}

export const locationDataSearchV2 = new LocationDataSearchV2()
